import {
  Input as HeadlessInput,
  type InputProps as HeadlessInputProps,
} from "@headlessui/react";
import { AlertCircle } from "@untitled-ui/icons-react";
import classNames from "classnames";
import {
  type ComponentRef,
  type JSX,
  type ReactNode,
  type RefAttributes,
  forwardRef,
  useContext,
} from "react";
import { twMerge } from "tailwind-merge";

import Description from "./Description";
import { FieldContext } from "./FieldContext";

export type InputProps = {
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  size?: keyof typeof sizes;
  transparent?: boolean;
  theme?: keyof typeof themes;
  description?: ReactNode;
  error?: string;
  className?: string;
} & Omit<HeadlessInputProps<"input">, "size" | "className">;

const sizes = {
  sm: "py-[7px] px-3 text-sm leading-6",
  md: "py-[9px] px-3.5 text-base",
};

const themes = {
  light: {
    base: "border bg-white shadow-xs",
    text: "text-gray-900 placeholder:text-gray-500",
    border: "border-gray-300 focus:border-primary-300 focus:ring-primary-100",
    error: "border-error-300 focus:ring-error-100",
  },
  dark: {
    base: "border bg-gray-950",
    text: "text-white placeholder:text-gray-500",
    border: "border-gray-800 focus:border-primary-600 focus:ring-primary-900",
    error: "border-error-800 focus:ring-error-900",
  },
};

const Input = forwardRef(function Input(
  {
    className,
    transparent,
    theme = "light",
    error,
    description,
    startIcon,
    endIcon,
    size = "md",
    dir,
    ...props
  }: InputProps,
  ref: React.Ref<HTMLElement>,
) {
  const fieldContext = useContext(FieldContext);
  const t = themes[theme];

  return (
    <div
      className={classNames(
        fieldContext.grid ? "sm:col-span-2" : undefined,
        "w-full min-w-0",
      )}
    >
      <div className="relative" dir={dir}>
        {startIcon && (
          <div className="absolute start-3 top-1/2 -translate-y-1/2 transform text-gray-400">
            {startIcon}
          </div>
        )}
        <HeadlessInput
          dir={dir}
          className={twMerge(
            transparent ? "" : t.base,
            "w-full rounded-lg focus:ring-4 focus:outline-hidden",
            t.text,
            !props.readOnly && (error ? t.error : t.border),
            startIcon !== undefined && "ps-10",
            (endIcon !== undefined || error) && "pe-10",
            "read-only:cursor-default read-only:rounded-lg read-only:border-transparent read-only:bg-gray-50 read-only:shadow-none read-only:focus:ring-0",
            "disabled:cursor-not-allowed disabled:rounded-lg disabled:border disabled:border-gray-300 disabled:bg-gray-50 disabled:text-gray-500",
            sizes[size],
            className,
          )}
          ref={ref}
          {...(props as any)}
        />
        {error ? (
          <div className="text-error-500 absolute end-3 top-1/2 -translate-y-1/2 transform">
            <AlertCircle className="h-4 w-4" />
          </div>
        ) : endIcon ? (
          <div className="absolute end-3 top-1/2 -translate-y-1/2 transform text-gray-400">
            {endIcon}
          </div>
        ) : null}
      </div>
      {(error || description) && (
        <Description variant={error ? "error" : "hint"} className="mt-1.5">
          {error || description}
        </Description>
      )}
    </div>
  );
}) as (props: InputProps & RefAttributes<ComponentRef<"input">>) => JSX.Element;

export default Input;
